import { Controller } from '@hotwired/stimulus'

/**
 * Scroll-triggered staggered animations
 *
 * Usage:
 *   <div data-controller="stagger-reveal"
 *        data-stagger-reveal-animation-value="fade-up"
 *        data-stagger-reveal-stagger-value="100"
 *        data-stagger-reveal-threshold-value="0.2">
 *     <div data-stagger-reveal-target="item">Item 1</div>
 *     <div data-stagger-reveal-target="item">Item 2</div>
 *     <div data-stagger-reveal-target="item">Item 3</div>
 *   </div>
 *
 * Values:
 *   - animation: CSS animation class to apply (default: "fade-up")
 *   - stagger: Delay between items in ms (default: 100)
 *   - threshold: IntersectionObserver threshold 0-1 (default: 0.2)
 *   - rootMargin: IntersectionObserver root margin (default: "0px")
 *   - once: Only animate once, don't reset (default: true)
 */
export default class extends Controller {
  static targets = ['item']

  static values = {
    animation: { type: String, default: 'fade-up' },
    stagger: { type: Number, default: 100 },
    threshold: { type: Number, default: 0.2 },
    rootMargin: { type: String, default: '0px' },
    once: { type: Boolean, default: true },
  }

  connect() {
    this.setupItems()
    this.createObserver()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupItems() {
    this.itemTargets.forEach((item, index) => {
      item.classList.add(this.animationValue)
      item.classList.add('stagger-reveal-hidden')
      item.style.setProperty('--stagger-delay', `${index * this.staggerValue}ms`)
    })
  }

  createObserver() {
    if (!('IntersectionObserver' in window)) {
      this.reveal()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        threshold: this.thresholdValue,
        rootMargin: this.rootMarginValue,
      },
    )

    this.observer.observe(this.element)
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.reveal()
        if (this.onceValue) {
          this.observer.unobserve(entry.target)
        }
      } else if (!this.onceValue) {
        this.hide()
      }
    })
  }

  reveal() {
    this.itemTargets.forEach((item) => {
      item.classList.remove('stagger-reveal-hidden')
      item.classList.add('go')
    })
  }

  hide() {
    this.itemTargets.forEach((item) => {
      item.classList.add('stagger-reveal-hidden')
      item.classList.remove('go')
    })
  }
}
